














import { Component, Prop, Vue, Watch } from "vue-property-decorator";

type CopyFeedbackState = "idle" | "copied" | "error";

@Component({
  name: "CopyToClipboardButton",
})
export default class CopyToClipboardButton extends Vue {
  @Prop({ required: true })
  public readonly value!: string;

  @Prop({ default: false })
  public readonly disabled!: boolean;

  @Prop({ default: false })
  public readonly isSmall!: boolean;

  @Prop({ default: "Copy" })
  public readonly idleText!: string;

  @Prop({ default: "Copied" })
  public readonly successText!: string;

  @Prop({ default: "Retry copy" })
  public readonly errorText!: string;

  @Prop({ default: 1800 })
  public readonly resetDelayMs!: number;

  @Prop({ default: "8rem" })
  public readonly minWidth!: string;

  private fCopyFeedbackResetTimeoutId?: number;
  private fFeedbackState: CopyFeedbackState = "idle";
  private fIsCopying = false;

  get buttonIconClass(): string {
    if (this.fFeedbackState === "copied") {
      return "fi-check";
    }

    if (this.fFeedbackState === "error") {
      return "fi-alert";
    }

    return "fi-page-copy";
  }

  get buttonStateClass(): string | undefined {
    if (this.fFeedbackState === "copied") {
      return "-copied";
    }

    if (this.fFeedbackState === "error") {
      return "-error";
    }

    return undefined;
  }

  get buttonStyle(): Record<string, string> {
    return {
      minWidth: this.minWidth,
    };
  }

  get buttonText(): string {
    if (this.fFeedbackState === "copied") {
      return this.successText;
    }

    if (this.fFeedbackState === "error") {
      return this.errorText;
    }

    return this.idleText;
  }

  public async copy(): Promise<void> {
    if (this.disabled || !this.value || this.fIsCopying) {
      return;
    }

    this.clearCopyFeedbackResetTimeout();
    this.fIsCopying = true;

    try {
      await navigator.clipboard.writeText(this.value);
      this.fFeedbackState = "copied";
      this.$emit("copied");
    } catch (e) {
      this.fFeedbackState = "error";
      this.$emit("copy-failed", e);
    } finally {
      this.fIsCopying = false;
    }

    this.scheduleCopyFeedbackReset();
  }

  protected beforeDestroy(): void {
    this.clearCopyFeedbackResetTimeout();
  }

  private clearCopyFeedbackResetTimeout(): void {
    if (!this.fCopyFeedbackResetTimeoutId) {
      return;
    }

    clearTimeout(this.fCopyFeedbackResetTimeoutId);
    this.fCopyFeedbackResetTimeoutId = undefined;
  }

  private resetCopyFeedback(): void {
    this.clearCopyFeedbackResetTimeout();
    this.fFeedbackState = "idle";
  }

  private scheduleCopyFeedbackReset(): void {
    this.clearCopyFeedbackResetTimeout();

    this.fCopyFeedbackResetTimeoutId = window.setTimeout(() => {
      this.fFeedbackState = "idle";
      this.fCopyFeedbackResetTimeoutId = undefined;
    }, this.resetDelayMs);
  }

  @Watch("value")
  private _onValueChanged(): void {
    this.resetCopyFeedback();
  }
}
